// File: src/components/common/DynamicIcon.jsx
import React, { useMemo, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Skeleton } from '@mui/material';
import NoteIcon from '@mui/icons-material/Note'; // Static fallback for errors
import { getIconEntry } from '../../config/icons';

// Cache for lazy components to prevent re-creating them on every render
const componentCache = new Map();

const getLazyComponent = (key) => {
  // Normalize the key to lowercase, defaulting to 'default' for null/undefined
  const normalizedKey = (key || 'default').toLowerCase();
  
  if (!componentCache.has(normalizedKey)) {
    const entry = getIconEntry(normalizedKey);
    
    // Create lazy component with error handling
    const LazyIcon = React.lazy(() => 
      entry.loader()
        .then(module => {
          // Handle both default and named exports
          const IconComponent = module.default || module[Object.keys(module)[0]];
          return { default: IconComponent };
        })
        .catch(err => {
          console.warn(`[DynamicIcon] Failed to load icon "${normalizedKey}":`, err);
          // Return fallback icon on error
          return { default: NoteIcon };
        })
    );
    
    componentCache.set(normalizedKey, LazyIcon);
  }
  
  return componentCache.get(normalizedKey);
};

const DynamicIcon = ({ icon, fallback, ...props }) => {
  const IconComponent = useMemo(() => getLazyComponent(icon), [icon]);

  return (
    <Suspense fallback={fallback || <Skeleton variant="circular" width={24} height={24} />}>
      <IconComponent {...props} />
    </Suspense>
  );
};

DynamicIcon.propTypes = {
  icon: PropTypes.string,
  fallback: PropTypes.node,
};

export default DynamicIcon;